import React, { useEffect, useState } from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";

const BORDER_COLOR = "#d1d5db";
const TEXT_BODY = "#374151";
const TEXT_MUTED = "#64748b";
const ACTIVE_GREEN = "#16a34a";
const ACTIVE_GREEN_DARK = "#15803d";

const baseButtonSx = {
  padding: "9px 20px",
  height: 40,
  borderRadius: "8px",
  border: `1.5px solid ${BORDER_COLOR}`,
  background: "white",
  color: TEXT_BODY,
  fontSize: 14,
  fontWeight: 600,
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  textTransform: "none",
};

const AppPagination = ({ count, page, onChange }) => {
  const theme = useTheme();
  const totalPages = Math.max(1, Number(count) || 1);
  const currentPage = Math.min(Math.max(1, Number(page) || 1), totalPages);

  const [inputValue, setInputValue] = useState(String(currentPage));

  useEffect(() => {
    setInputValue(String(currentPage));
  }, [currentPage]);

  const commitPage = (raw) => {
    const parsed = parseInt(String(raw).trim(), 10);
    const valid = !Number.isNaN(parsed) && parsed >= 1 && parsed <= totalPages;

    if (!valid) {
      setInputValue(String(currentPage));
      return;
    }

    setInputValue(String(parsed));
    if (parsed !== currentPage) {
      onChange?.(null, parsed);
    }
  };

  const goPrev = () => {
    if (currentPage <= 1) return;
    onChange?.(null, currentPage - 1);
  };

  const goNext = () => {
    if (currentPage >= totalPages) return;
    onChange?.(null, currentPage + 1);
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        width: "100%",
        flexWrap: "wrap",
      }}
    >
      <Button
        variant="contained"
        onClick={goPrev}
        disabled={currentPage === 1}
        sx={{
          ...baseButtonSx,
          border: "none",
          bgcolor: theme.palette.primary.main,
          color: "#fff",
          "&:hover": currentPage === 1 ? {} : { bgcolor: theme.palette.primary.dark },
          "&.Mui-disabled": { opacity: 0.4, cursor: "not-allowed" },
        }}
      >
        ← Previous
      </Button>

      <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 500, color: TEXT_MUTED }}>
        Page
      </Typography>

      <Box
        component="input"
        type="number"
        value={inputValue}
        min={1}
        max={totalPages}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitPage(inputValue);
          }
        }}
        onBlur={() => commitPage(inputValue)}
        aria-label="Page number"
        sx={{
          width: "50px",
          height: "38px",
          borderRadius: "8px",
          border: `1.5px solid ${BORDER_COLOR}`,
          background: "white",
          textAlign: "center",
          fontSize: 14,
          fontWeight: 600,
          color: "#1e293b",
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          outline: "none",
          padding: 0,
          boxSizing: "border-box",
          transition: "all 0.15s ease",
          MozAppearance: "textfield",
          "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
          },
          "&:focus": {
            borderColor: ACTIVE_GREEN,
            boxShadow: "0 0 0 3px rgba(22,163,74,0.15)",
          },
        }}
      />

      <Typography variant="body2" sx={{ fontSize: 14, fontWeight: 500, color: TEXT_MUTED }}>
        of{" "}
        <Box component="span" sx={{ fontWeight: 700, color: theme.palette.text.primary, fontSize: 14 }}>
          {totalPages}
        </Box>
      </Typography>

      <Button
        variant="contained"
        onClick={goNext}
        disabled={currentPage === totalPages}
        sx={{
          ...baseButtonSx,
          border: "none",
          bgcolor: theme.palette.primary.main,
          color: "#fff",
          "&:hover": currentPage === totalPages ? {} : { bgcolor: theme.palette.primary.dark },
          "&.Mui-disabled": { opacity: 0.4, cursor: "not-allowed" },
        }}
      >
        Next →
      </Button>
    </Box>
  );
};

export default AppPagination;